import { HandlerEvent } from 'post-robot';
import { error, ValidationError } from 'common/errors';
import { pushErrors } from 'common/gamp';
import { saveMeta, saveUser } from 'communicator/store';
import {
  getApiKey,
  getFeatureToggles,
  getIsVisaSRCi,
  getOrgId,
  getSessionId,
  getThmProfileHint
} from 'communicator/store/selectors';
import fetchIdLookup from 'communicator/utils/api/id-lookup';
import getGenericError from 'communicator/utils/generic-error';
import { getLockoutFlow } from 'communicator/utils/get-lockout-flow';
import { markEnd } from 'communicator/utils/instrumentation';
import createSessionId from 'communicator/utils/session-id';
import { localStorage, STARTING_TAB, WELCOME_TABS } from 'communicator/utils/storage';
import { loadThm } from 'communicator/utils/thm';
import { IdentityLookupPayload, IdLookupData } from 'types/visa-sdk/identity-lookup';

export default async function identityLookupLegacy(
  event: HandlerEvent<IdLookupData>
): Promise<IdentityLookupPayload | ValidationError> {
  try {
    const { data } = event;
    const { enableVpps } = getFeatureToggles();
    const lookupResponse = await fetchIdLookup({
      apikey: getApiKey(),
      email: data.consumerIdentity.identityValue,
      sessionId: getSessionId(),
      thmProfileHint: getThmProfileHint(),
      usernameType: 'EMAIL'
    });

    const lookupStatus = lookupResponse.ok
      ? typeof lookupResponse.data.data?.actions === 'undefined'
        ? 'PRESENT'
        : lookupResponse.data.data.actions[0] === 'ENROLL'
          ? 'NOTPRESENT'
          : 'USERNOTALLOWED'
      : undefined;

    const lockoutFlow = getLockoutFlow(lookupResponse);
    const isLocked = Boolean(lockoutFlow);

    // VCO-15249 - Relaunch THM with new sessionId if account is locked
    if (isLocked) {
      saveMeta({
        sessionId: createSessionId(enableVpps),
        thmProfileHint: null
      });

      loadThm({
        isVisaSrci: getIsVisaSRCi(),
        orgId: getOrgId(),
        sessionId: getSessionId()
      });
    }

    if (lookupStatus === 'USERNOTALLOWED') {
      localStorage.setItem(STARTING_TAB, WELCOME_TABS.SIGN_IN);
    }

    if (!lookupResponse.ok || lookupStatus === 'USERNOTALLOWED') {
      if (lockoutFlow) {
        markEnd('IDENTITY_LOOKUP', { doFlush: true, failure: true, reason: 'ACCT_INACCESSIBLE' });
        // START GENAI@GHCOPILOT
        pushErrors('IDENTITY_LOOKUP_LEGACY', 'ACCT_INACCESSIBLE');
        // END GENAI@GHCOPILOT
        return error('ACCT_INACCESSIBLE');
      }

      if (!lookupResponse.ok) {
        const err = getGenericError(lookupResponse);
        markEnd('IDENTITY_LOOKUP', { doFlush: true, failure: true, reason: err });
        // START GENAI@GHCOPILOT
        pushErrors('IDENTITY_LOOKUP_LEGACY', err);
        // END GENAI@GHCOPILOT
        return error(err);
      }

      markEnd('IDENTITY_LOOKUP', { doFlush: true, failure: true, reason: 'UNKNOWN_ERROR' });
      // START GENAI@GHCOPILOT
      pushErrors('IDENTITY_LOOKUP_LEGACY', 'UNKNOWN_ERROR');
      // END GENAI@GHCOPILOT
      return error('UNKNOWN_ERROR');
    }

    const consumerPresent = lookupStatus === 'PRESENT';
    const payload: IdentityLookupPayload = { consumerPresent };

    if (consumerPresent) {
      localStorage.setItem(STARTING_TAB, WELCOME_TABS.SIGN_IN);
    }

    saveUser({
      accessToken: consumerPresent
        ? {
            expiresIn: lookupResponse.data.expires_in,
            isPartial: true,
            token: lookupResponse.data.access_token
          }
        : null,
      country: lookupResponse.data.data?.locality?.country,
      email: data.consumerIdentity.identityValue,
      isLocked,
      locale: lookupResponse.data.data?.locality?.locale,
      lookupStatus
    });

    if (getIsVisaSRCi() && lookupResponse.data.data) {
      payload.customData = {
        data: {
          country: lookupResponse.data.data.locality?.country,
          locale: lookupResponse.data.data.locality?.locale
        }
      };
    }

    markEnd('IDENTITY_LOOKUP_LEGACY');
    return payload;
  } catch (err: unknown) {
    pushErrors('IDENTITY_LOOKUP_LEGACY', err);
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    const sErr = JSON.stringify(err) !== undefined ? JSON.stringify(err) : 'Unknown error';
    markEnd('IDENTITY_LOOKUP_LEGACY', { doFlush: true, failure: true, reason: sErr });
    return error('IDENTITY_LOOKUP_LEGACY_ERROR', sErr);
  }
}
